import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Check, ArrowLeftRight } from 'lucide-react';

const comparisons = [
  {
    aspect: 'Trip Management',
    old: 'Broker-based fragmentation',
    new: 'Direct AI facilitation',
    oldDetail: 'Multiple intermediaries, delayed communication, lost context',
    newDetail: 'Single unified platform with real-time AI coordination'
  },
  {
    aspect: 'Dispatch Method',
    old: 'Manual dispatch',
    new: 'Conversational AI',
    oldDetail: 'Phone calls, spreadsheets, human error',
    newDetail: 'Natural language processing, voice/text input, zero wait time'
  },
  {
    aspect: 'Payment Speed',
    old: 'Delayed payments (30-90 days)',
    new: 'Instant payouts',
    oldDetail: 'Net-30/60/90 terms, cash flow problems for drivers',
    newDetail: 'Same-day or instant payment after trip completion'
  },
  {
    aspect: 'System Integration',
    old: 'Fragmented systems',
    new: 'Unified platform',
    oldDetail: 'Different software for dispatch, billing, compliance',
    newDetail: 'Single ecosystem for all operations'
  },
  {
    aspect: 'Compliance Tracking',
    old: 'Manual verification',
    new: 'Automated credentialing',
    oldDetail: 'Paper-based, prone to lapses, audit nightmares',
    newDetail: 'Real-time monitoring, automatic alerts, always audit-ready'
  },
  {
    aspect: 'Safety Monitoring',
    old: 'Post-incident review',
    new: 'AI-powered prevention',
    oldDetail: 'React to problems after they happen',
    newDetail: 'Real-time AI cameras detect and prevent incidents'
  },
];

export const DisruptionSection = () => {
  const [hoveredRow, setHoveredRow] = useState(null);

  return (
    <section 
      id="disruption" 
      className="relative py-24 lg:py-32 overflow-hidden"
      data-testid="disruption-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-medex-bg via-medex-surface/30 to-medex-bg" />
      
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1 rounded-full bg-white/5 border border-white/10 text-sm font-mono text-slate-400 uppercase tracking-wider mb-6">
            Industry Disruption
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            The Old Way vs MedexOVA
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-slate-400">
            See how we're transforming every aspect of healthcare transportation.
          </p>
        </motion.div>

        {/* Comparison Table */}
        <div className="space-y-4">
          {/* Header */}
          <div className="grid grid-cols-3 gap-4 px-4 mb-2">
            <div className="text-sm font-mono text-slate-500 uppercase tracking-wider">
              Aspect
            </div>
            <div className="text-sm font-mono text-slate-500 uppercase tracking-wider text-center">
              Old Model
            </div>
            <div className="text-sm font-mono text-neon-cyan uppercase tracking-wider text-center">
              MedexOVA
            </div>
          </div>

          {/* Rows */}
          {comparisons.map((row, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`
                glass-card p-4 lg:p-6 cursor-pointer
                transition-all duration-300
                ${hoveredRow === index ? 'border-neon-cyan/30 shadow-glow-cyan' : ''}
              `}
              onMouseEnter={() => setHoveredRow(index)}
              onMouseLeave={() => setHoveredRow(null)}
              data-testid={`comparison-row-${index}`}
            >
              <div className="grid grid-cols-3 gap-4 items-center">
                {/* Aspect */}
                <div>
                  <h3 className="text-base lg:text-lg font-heading font-semibold text-white">
                    {row.aspect}
                  </h3>
                </div>

                {/* Old Way */}
                <div className="text-center">
                  <div className="inline-flex items-center space-x-2 px-3 py-1 bg-red-500/10 border border-red-500/20 rounded-full mb-2">
                    <X className="w-3 h-3 text-red-400" />
                    <span className="text-sm text-red-400">{row.old}</span>
                  </div>
                  <motion.p
                    initial={false}
                    animate={{ 
                      height: hoveredRow === index ? 'auto' : 0,
                      opacity: hoveredRow === index ? 1 : 0 
                    }}
                    className="text-xs text-slate-500 overflow-hidden"
                  >
                    {row.oldDetail}
                  </motion.p>
                </div>

                {/* New Way */}
                <div className="text-center">
                  <div className="inline-flex items-center space-x-2 px-3 py-1 bg-neon-cyan/10 border border-neon-cyan/20 rounded-full mb-2">
                    <Check className="w-3 h-3 text-neon-cyan" />
                    <span className="text-sm text-neon-cyan">{row.new}</span>
                  </div>
                  <motion.p
                    initial={false}
                    animate={{ 
                      height: hoveredRow === index ? 'auto' : 0,
                      opacity: hoveredRow === index ? 1 : 0 
                    }}
                    className="text-xs text-slate-400 overflow-hidden"
                  >
                    {row.newDetail}
                  </motion.p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Broker vs Facilitator Explanation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 glass-card p-8"
          data-testid="broker-facilitator-section"
        >
          <div className="flex items-center justify-center space-x-4 mb-6">
            <span className="text-lg font-heading font-bold text-slate-400">Broker</span>
            <ArrowLeftRight className="w-6 h-6 text-neon-purple" />
            <span className="text-lg font-heading font-bold text-neon-cyan">Facilitator</span>
          </div>
          
          <h3 className="text-2xl font-heading font-bold text-white text-center mb-6">
            Working Together, Not Competing
          </h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 bg-slate-800/30 rounded-xl border border-slate-700/50">
              <h4 className="text-lg font-heading font-semibold text-white mb-3">Traditional Broker Model</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>• Contracts with providers and insurance</li>
                <li>• Subcontracts trips to transportation companies</li>
                <li>• Acts as middleman in the transaction</li>
                <li>• Important role in network aggregation</li>
              </ul>
            </div>
            
            <div className="p-6 bg-neon-cyan/5 rounded-xl border border-neon-cyan/20">
              <h4 className="text-lg font-heading font-semibold text-neon-cyan mb-3">MedexOVA Facilitator Model</h4>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Works parallel to brokers, not against them</li>
                <li>• Direct services to providers & driver networks</li>
                <li>• Manages and assists clients directly</li>
                <li>• Ensures highest quality of care standards</li>
              </ul>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-neon-purple/10 rounded-lg border border-neon-purple/20">
            <p className="text-center text-slate-300">
              <span className="text-neon-purple font-semibold">Our Advantage:</span> Both models can coexist. Brokers can contract with us, 
              while we also provide direct facilitation. Our infrastructure supports <span className="text-white font-semibold">both pathways</span> with 
              superior technology, instant payments, and AI-powered operations.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default DisruptionSection;
